import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Play,
  Pause,
  Square,
  Edit,
  Trash2,
  ExternalLink,
  Clock,
  User,
  Globe
} from 'lucide-react'
import { api } from '../lib/api'
import { formatTimestamp, getStatusVariant } from '../lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'

export function WorkflowDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: workflow, isLoading } = useQuery({
    queryKey: ['workflow', id],
    queryFn: () => api.getWorkflow(Number(id)),
    enabled: !!id,
  })

  const startWorkflowMutation = useMutation({
    mutationFn: () => api.startWorkflow(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow', id] })
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
    },
  })

  const pauseWorkflowMutation = useMutation({
    mutationFn: () => api.pauseWorkflow(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow', id] })
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
    },
  })

  const resumeWorkflowMutation = useMutation({
    mutationFn: () => api.resumeWorkflow(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow', id] })
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
    },
  })

  const stopWorkflowMutation = useMutation({
    mutationFn: () => api.stopWorkflow(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow', id] })
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
    },
  })

  const deleteWorkflowMutation = useMutation({
    mutationFn: () => api.deleteWorkflow(Number(id)),
    onSuccess: () => {
      navigate('/workflows')
    },
  })

  const handleStart = () => startWorkflowMutation.mutate()
  const handlePause = () => pauseWorkflowMutation.mutate()
  const handleResume = () => resumeWorkflowMutation.mutate()
  const handleStop = () => stopWorkflowMutation.mutate()
  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this workflow?')) {
      deleteWorkflowMutation.mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-48" />
          </div>
          <Skeleton className="h-64" />
        </div>
      </div>
    )
  }

  if (!workflow) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold">Workflow not found</h2>
        <p className="text-muted-foreground mt-2">
          The workflow you're looking for doesn't exist.
        </p>
        <Button asChild className="mt-4">
          <Link to="/workflows">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Workflows
          </Link>
        </Button>
      </div>
    )
  }

  const canStart = ['created', 'paused', 'stopped', 'failed'].includes(workflow.status)
  const canPause = workflow.status === 'running'
  const canResume = workflow.status === 'paused'
  const canStop = ['running', 'paused'].includes(workflow.status)
  const canDelete = workflow.status !== 'running'

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/workflows">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{workflow.name}</h1>
            {workflow.description && (
              <p className="text-muted-foreground">{workflow.description}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Badge variant={getStatusVariant(workflow.status)} className="text-sm">
            {workflow.status}
          </Badge>
          
          {canStart && (
            <Button onClick={handleStart} disabled={startWorkflowMutation.isPending}>
              <Play className="h-4 w-4 mr-2" />
              Start
            </Button>
          )}
          
          {canPause && (
            <Button onClick={handlePause} disabled={pauseWorkflowMutation.isPending}>
              <Pause className="h-4 w-4 mr-2" />
              Pause
            </Button>
          )}
          
          {canResume && (
            <Button onClick={handleResume} disabled={resumeWorkflowMutation.isPending}>
              <Play className="h-4 w-4 mr-2" />
              Resume
            </Button>
          )}
          
          {canStop && (
            <Button 
              variant="destructive" 
              onClick={handleStop} 
              disabled={stopWorkflowMutation.isPending}
            >
              <Square className="h-4 w-4 mr-2" />
              Stop
            </Button>
          )}
          
          <Button variant="outline" asChild>
            <Link to={`/workflows/${workflow.id}/edit`}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Link>
          </Button>
          
          {canDelete && (
            <Button 
              variant="outline" 
              onClick={handleDelete}
              disabled={deleteWorkflowMutation.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {/* Workflow Details */}
          <Card>
            <CardHeader>
              <CardTitle>Workflow Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Target URL:</span>
                <a 
                  href={workflow.base_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center"
                >
                  {workflow.base_url}
                  <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </div>
              
              {workflow.persona_name && (
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">AI Persona:</span>
                  <span className="text-sm">{workflow.persona_name}</span>
                </div>
              )}
              
              {workflow.cron && (
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Schedule:</span>
                  <code className="text-sm bg-muted px-2 py-1 rounded">{workflow.cron}</code>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Execution History Placeholder */}
          <Card>
            <CardHeader>
              <CardTitle>Execution History</CardTitle>
              <CardDescription>
                Recent workflow executions and their results
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="mx-auto h-8 w-8 mb-2" />
                <p>Execution history will be available soon</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="text-sm font-medium text-muted-foreground">Created</div>
                <div className="text-sm">{formatTimestamp(new Date(workflow.created_at).getTime() / 1000)}</div>
              </div>
              
              <Separator />
              
              <div>
                <div className="text-sm font-medium text-muted-foreground">Last Updated</div>
                <div className="text-sm">{formatTimestamp(new Date(workflow.updated_at).getTime() / 1000)}</div>
              </div>
              
              {workflow.started_at && (
                <>
                  <Separator />
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Started</div>
                    <div className="text-sm">{formatTimestamp(new Date(workflow.started_at).getTime() / 1000)}</div>
                  </div>
                </>
              )}
              
              {workflow.finished_at && (
                <>
                  <Separator />
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Finished</div>
                    <div className="text-sm">{formatTimestamp(new Date(workflow.finished_at).getTime() / 1000)}</div>
                  </div>
                </>
              )}
              
              {workflow.duration_ms && (
                <>
                  <Separator />
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Duration</div>
                    <div className="text-sm">{Math.round(workflow.duration_ms / 1000)}s</div>
                  </div>
                </>
              )}
              
              {workflow.created_by_email && (
                <>
                  <Separator />
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Created By</div>
                    <div className="text-sm">{workflow.created_by_email}</div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
